import { useCallback, useRef, useState } from "react";
import type { ConfirmDialogProps } from "./ConfirmDialog";

type ConfirmOptions = {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
};

type PendingConfirm = ConfirmOptions & {
  resolve: (value: boolean) => void;
};

/**
 * Promise-based confirm for replacing window.confirm.
 * Render `{dialogProps && <ConfirmDialog {...dialogProps} />}` in the tree.
 */
export function useConfirmDialog() {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const pendingRef = useRef<PendingConfirm | null>(null);

  const close = useCallback((result: boolean) => {
    const current = pendingRef.current;
    pendingRef.current = null;
    setPending(null);
    current?.resolve(result);
  }, []);

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      const next: PendingConfirm = { ...options, resolve };
      pendingRef.current = next;
      setPending(next);
    });
  }, []);

  const dialogProps: ConfirmDialogProps | null = pending
    ? {
        open: true,
        title: pending.title,
        message: pending.message,
        confirmLabel: pending.confirmLabel,
        cancelLabel: pending.cancelLabel,
        destructive: pending.destructive,
        onConfirm: () => close(true),
        onCancel: () => close(false),
      }
    : null;

  return { confirm, dialogProps };
}
